import { useRef, useEffect, useState } from 'react'
import { getStreamFromMagnet, continueWithSelectedFile } from '../../services/realdebrid'
import { getSubtitles, filterSubtitlesByLanguage, isSubdlAvailable, setSubdlApiKey } from '../../services/subtitles'
import { addWatchedMovie, isMovieWatched } from '../../services/movies'
import { auth } from '../../services/firebase'
import './DebridPlayer.css'

function DebridPlayer({ magnetUrl, title, imdbId, movieData, onClose }) {
  const videoRef = useRef(null)
  const wrapperRef = useRef(null)
  const [status, setStatus] = useState('loading')
  const [progress, setProgress] = useState('')
  const [streamUrl, setStreamUrl] = useState(null)
  const [error, setError] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [subtitles, setSubtitles] = useState([])
  const [selectedSubtitle, setSelectedSubtitle] = useState(null)
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false)
  const [subtitleOffset, setSubtitleOffset] = useState(0)
  const [subtitleSource, setSubtitleSource] = useState('opensubtitles')
  const [showApiKeyInput, setShowApiKeyInput] = useState(false)
  const [apiKeyInput, setApiKeyInput] = useState('')
  const [loadingSubtitles, setLoadingSubtitles] = useState(false)
  const [subtitleLangFilter, setSubtitleLangFilter] = useState('all')
  const [audioTracks, setAudioTracks] = useState([])
  const [selectedAudioTrack, setSelectedAudioTrack] = useState(null)
  
  // Subtitle styling
  const [subtitleStyle, setSubtitleStyle] = useState({
    fontSize: localStorage.getItem('subFontSize') || '24',
    color: localStorage.getItem('subColor') || '#ffffff',
    outline: localStorage.getItem('subOutline') || 'black',
  })
  
  // Watch tracking
  const [hasMarkedWatched, setHasMarkedWatched] = useState(false)
  
  // Advanced subtitle sync
  const [showAdvancedSync, setShowAdvancedSync] = useState(false)
  const [parsedCues, setParsedCues] = useState([])
  const [currentVideoTime, setCurrentVideoTime] = useState(0)
  const syncListRef = useRef(null)
  
  // Pack file selection
  const [packFiles, setPackFiles] = useState(null)
  const [packTorrentId, setPackTorrentId] = useState(null)
  const [packSearchQuery, setPackSearchQuery] = useState('')

  useEffect(() => {
    let mounted = true

    const getStream = async () => {
      setStatus('loading')
      
      const result = await getStreamFromMagnet(magnetUrl, (progressMsg) => {
        if (mounted) setProgress(progressMsg)
      })

      if (!mounted) return

      if (result.error) {
        setError(result.error)
        setStatus('error')
      } else if (result.isPack) {
        // Movie pack detected - show file selector
        setPackFiles(result.files)
        setPackTorrentId(result.torrentId)
        setStatus('pack_selection')
      } else {
        setStreamUrl(result.streamUrl)
        setStatus('ready')
      }
    }

    getStream()

    return () => {
      mounted = false
    }
  }, [magnetUrl])

  // Fetch subtitles when imdbId or source changes
  useEffect(() => {
    const fetchSubtitles = async () => {
      if (!imdbId) return
      if (subtitleSource === 'subdl' && !isSubdlAvailable()) return
      
      setLoadingSubtitles(true)
      setSubtitles([])
      
      const subs = await getSubtitles(imdbId, subtitleSource)
      const filtered = filterSubtitlesByLanguage(subs, ['spa', 'eng', 'es', 'en'])
      setSubtitles(filtered)
      setLoadingSubtitles(false)
    }
    fetchSubtitles()
  }, [imdbId, subtitleSource])

  // Auto-play when stream is ready
  useEffect(() => {
    if (status === 'ready' && videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }, [status])

  // Auto-mark as watched after watching for 10 minutes
  useEffect(() => {
    if (status !== 'ready' || !videoRef.current || !movieData || hasMarkedWatched) return
    
    const video = videoRef.current
    
    const handleTimeUpdate = async () => {
      const watchTime = video.currentTime
      
      // Mark as watched after 10 minutes (600 seconds)
      if (watchTime >= 600 && !hasMarkedWatched) {
        setHasMarkedWatched(true)
        
        const user = auth.currentUser
        if (!user) {
          console.log('No user logged in, skipping watch tracking')
          return
        }
        
        // Check if already watched
        try {
          const alreadyWatched = await isMovieWatched(user.uid, movieData.tmdbId)
          if (alreadyWatched) {
            console.log('Movie already in watched list')
            return
          }
          
          // Add to watched list
          await addWatchedMovie(user.uid, movieData, {
            date: new Date().toISOString().split('T')[0],
            rating: 0,
            notes: 'Vista a través de streaming',
          })
          console.log('✅ Movie automatically marked as watched!')
        } catch (err) {
          console.error('Error marking movie as watched:', err)
        }
      }
    }
    
    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => video.removeEventListener('timeupdate', handleTimeUpdate)
  }, [status, movieData, hasMarkedWatched])

  // Update subtitle style and persist to localStorage
  const updateSubtitleStyle = (key, value) => {
    setSubtitleStyle(prev => ({ ...prev, [key]: value }))
    localStorage.setItem(`sub${key.charAt(0).toUpperCase() + key.slice(1)}`, value)
  }

  // Apply subtitle styling dynamically via injected style
  useEffect(() => {
    const styleId = 'debrid-subtitle-style'
    let styleEl = document.getElementById(styleId)
    
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }
    
    const textShadow = subtitleStyle.outline === 'none' 
      ? 'none' 
      : '2px 2px 4px #000, -2px -2px 4px #000, 2px -2px 4px #000, -2px 2px 4px #000'
    
    styleEl.textContent = `
      .debrid-video::cue {
        font-size: ${subtitleStyle.fontSize}px;
        color: ${subtitleStyle.color};
        text-shadow: ${textShadow};
        background-color: transparent;
      }
    `
    
    return () => {
      const el = document.getElementById(styleId)
      if (el) el.remove()
    }
  }, [subtitleStyle])

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isFullscreen) {
        onClose()
      }
      if (e.key === 'f' && videoRef.current) {
        toggleFullscreen()
      }
      if (e.key === 's') {
        setShowSubtitleMenu(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, isFullscreen])

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Detect audio tracks when video loads
  useEffect(() => {
    if (!videoRef.current || status !== 'ready') return
    
    const video = videoRef.current
    
    const detectAudioTracks = () => {
      const tracks = video.audioTracks
      if (tracks && tracks.length > 0) {
        const trackList = []
        for (let i = 0; i < tracks.length; i++) {
          trackList.push({
            index: i,
            id: tracks[i].id,
            language: tracks[i].language || 'Unknown',
            label: tracks[i].label || `Track ${i + 1}`,
            enabled: tracks[i].enabled
          })
        }
        setAudioTracks(trackList)
        const enabledTrack = trackList.find(t => t.enabled)
        if (enabledTrack) setSelectedAudioTrack(enabledTrack.index)
      }
    }
    
    // Try detecting immediately and also on loadedmetadata
    video.addEventListener('loadedmetadata', detectAudioTracks)
    detectAudioTracks()
    
    return () => {
      video.removeEventListener('loadedmetadata', detectAudioTracks)
    }
  }, [status])

  // Handle audio track change
  const handleAudioTrackChange = (trackIndex) => {
    if (!videoRef.current || !videoRef.current.audioTracks) return
    
    const tracks = videoRef.current.audioTracks
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].enabled = (i === trackIndex)
    }
    setSelectedAudioTrack(trackIndex)
  }

  const toggleFullscreen = () => {
    const overlay = document.querySelector('.debrid-player-overlay')
    
    if (!overlay) return
    
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (overlay.requestFullscreen) {
        overlay.requestFullscreen().catch(err => console.error('Fullscreen error:', err))
      } else if (overlay.webkitRequestFullscreen) {
        overlay.webkitRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    }
  }

  // Helper function to apply offset to VTT timecodes
  const applyOffset = (vttContent, offsetSeconds) => {
    if (offsetSeconds === 0) return vttContent
    
    return vttContent.replace(
      /(\d{2}):(\d{2}):(\d{2})\.(\d{3})/g,
      (match, h, m, s, ms) => {
        let totalMs = parseInt(h) * 3600000 + parseInt(m) * 60000 + parseInt(s) * 1000 + parseInt(ms)
        totalMs += offsetSeconds * 1000
        if (totalMs < 0) totalMs = 0
        
        const hours = Math.floor(totalMs / 3600000)
        const mins = Math.floor((totalMs % 3600000) / 60000)
        const secs = Math.floor((totalMs % 60000) / 1000)
        const millis = totalMs % 1000
        
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(millis).padStart(3, '0')}`
      }
    )
  }

  // Parse VTT content into individual cues
  const parseVttCues = (vttContent) => {
    const cues = []
    const cueRegex = /(\d{2}:\d{2}:\d{2}[.,]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[.,]\d{3})\s*\n([\s\S]*?)(?=\n\n|\n\d{2}:\d{2}:\d{2}|$)/g
    let match
    
    const parseTime = (timeStr) => {
      const [h, m, rest] = timeStr.split(':')
      const [s, ms] = rest.replace(',', '.').split('.')
      return parseInt(h) * 3600 + parseInt(m) * 60 + parseInt(s) + parseInt(ms) / 1000
    }
    
    while ((match = cueRegex.exec(vttContent)) !== null) {
      const text = match[3].replace(/<[^>]*>/g, '').trim() // Remove HTML tags
      if (text) {
        cues.push({
          start: parseTime(match[1]),
          end: parseTime(match[2]),
          text: text,
          startFormatted: match[1].replace(',', '.')
        })
      }
    }
    
    return cues
  }

  // Track video time for advanced sync
  useEffect(() => {
    if (!showAdvancedSync || !videoRef.current) return
    
    const video = videoRef.current
    const updateTime = () => setCurrentVideoTime(video.currentTime)
    
    video.addEventListener('timeupdate', updateTime)
    return () => video.removeEventListener('timeupdate', updateTime)
  }, [showAdvancedSync])

  // Get current cue index for highlighting (no auto-scroll)
  const getCurrentCueIndex = () => {
    if (parsedCues.length === 0) return -1
    const expectedTime = currentVideoTime - subtitleOffset
    return parsedCues.findIndex(cue => 
      expectedTime >= cue.start && expectedTime <= cue.end
    )
  }

  // Manual scroll to current cue
  const scrollToCurrentCue = () => {
    const currentIndex = getCurrentCueIndex()
    if (currentIndex >= 0 && syncListRef.current?.children[currentIndex]) {
      syncListRef.current.children[currentIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }

  // Toggle play/pause from sync modal
  const togglePlayPause = () => {
    if (!videoRef.current) return
    if (videoRef.current.paused) {
      videoRef.current.play()
    } else {
      videoRef.current.pause()
    }
  }

  // Check if video is paused
  const [isVideoPaused, setIsVideoPaused] = useState(false)
  
  useEffect(() => {
    if (!videoRef.current) return
    
    const updatePausedState = () => setIsVideoPaused(videoRef.current.paused)
    const video = videoRef.current
    
    video.addEventListener('play', updatePausedState)
    video.addEventListener('pause', updatePausedState)
    
    return () => {
      video.removeEventListener('play', updatePausedState)
      video.removeEventListener('pause', updatePausedState)
    }
  }, [status])

  // Handle cue click for sync
  const handleSyncCueClick = (cue) => {
    if (!videoRef.current) return
    const currentTime = videoRef.current.currentTime
    const newOffset = currentTime - cue.start
    setSubtitleOffset(parseFloat(newOffset.toFixed(1)))
    setShowAdvancedSync(false)
  }

  // Open advanced sync modal
  const openAdvancedSync = () => {
    if (selectedSubtitle?.rawVtt) {
      const cues = parseVttCues(selectedSubtitle.rawVtt)
      setParsedCues(cues)
      setShowAdvancedSync(true)
    }
  }

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }

  // Handle pack file selection
  const handlePackFileSelect = async (file) => {
    setStatus('loading')
    setProgress('Procesando archivo seleccionado...')
    
    const result = await continueWithSelectedFile(packTorrentId, file.id, (progressMsg) => {
      setProgress(progressMsg)
    })
    
    if (result.error) {
      setError(result.error)
      setStatus('error')
    } else {
      setStreamUrl(result.streamUrl)
      setPackFiles(null)
      setPackTorrentId(null)
      setStatus('ready')
    }
  }

  // Get filtered and sorted pack files
  const getFilteredPackFiles = () => {
    if (!packFiles) return []
    
    let filtered = packFiles
    
    // Apply search filter
    if (packSearchQuery) {
      const query = packSearchQuery.toLowerCase()
      filtered = filtered.filter(f => 
        f.filename.toLowerCase().includes(query) ||
        f.path.toLowerCase().includes(query)
      )
    }
    
    // Sort: recommended match first, then alphabetically
    const movieTitle = title?.toLowerCase() || ''
    const titleWords = movieTitle.split(/\s+/).filter(w => w.length > 2)
    
    return filtered.map(f => {
      const filename = f.filename.toLowerCase()
      // Check how many title words match
      const matchScore = titleWords.reduce((score, word) => {
        return filename.includes(word) ? score + 1 : score
      }, 0)
      return { ...f, matchScore }
    }).sort((a, b) => {
      // Higher match score first
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore
      // Then alphabetically
      return a.filename.localeCompare(b.filename)
    })
  }

  const handleSubtitleSelect = async (subtitle) => {
    setShowSubtitleMenu(false)
    setSubtitleOffset(0) // Reset offset when selecting new subtitle
    
    if (!subtitle) {
      setSelectedSubtitle(null)
      return
    }
    
    try {
      let text = ''
      
      // Check if it's a ZIP file (Subdl)
      if (subtitle.isZip) {
        // Dynamic import of JSZip
        const JSZip = (await import('jszip')).default
        
        const response = await fetch(subtitle.url)
        const zipData = await response.arrayBuffer()
        
        const zip = await JSZip.loadAsync(zipData)
        
        // Find SRT file in the ZIP
        const srtFile = Object.keys(zip.files).find(name => 
          name.toLowerCase().endsWith('.srt')
        )
        
        if (!srtFile) {
          console.error('No SRT file found in ZIP')
          return
        }
        
        text = await zip.files[srtFile].async('string')
      } else {
        // Regular fetch for OpenSubtitles
        const response = await fetch(subtitle.url)
        text = await response.text()
      }
      
      // Convert SRT to VTT format if needed
      let vttContent = text
      if (!text.startsWith('WEBVTT')) {
        vttContent = 'WEBVTT\n\n' + text.replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2')
      }
      
      // Create Blob URL
      const blob = new Blob([vttContent], { type: 'text/vtt' })
      const blobUrl = URL.createObjectURL(blob)
      
      setSelectedSubtitle({
        ...subtitle,
        rawVtt: vttContent,
        blobUrl
      })
    } catch (error) {
      console.error('Error loading subtitle:', error)
    }
  }
  
  // Re-apply offset when it changes
  useEffect(() => {
    if (!selectedSubtitle?.rawVtt) return
    
    const offsetVtt = applyOffset(selectedSubtitle.rawVtt, subtitleOffset)
    const blob = new Blob([offsetVtt], { type: 'text/vtt' })
    const newBlobUrl = URL.createObjectURL(blob)
    
    // Revoke old URL
    if (selectedSubtitle.blobUrl) {
      URL.revokeObjectURL(selectedSubtitle.blobUrl)
    }
    
    setSelectedSubtitle(prev => ({
      ...prev,
      blobUrl: newBlobUrl
    }))
  }, [subtitleOffset])

  return (
    <div className="debrid-player-overlay" onClick={onClose}>
      <div className="debrid-player-container" onClick={(e) => e.stopPropagation()}>
        {!isFullscreen && (
          <div className="debrid-player-header">
            <h3>{title}</h3>
            <button className="debrid-player-close" onClick={onClose}>✕</button>
          </div>
        )}
        
        <div className="debrid-player-wrapper" ref={wrapperRef}>
          {status === 'loading' && (
            <div className="debrid-player-status">
              <div className="spinner"></div>
              <p>{progress || 'Conectando con Real-Debrid...'}</p>
            </div>
          )}
          
          {status === 'error' && (
            <div className="debrid-player-status error">
              <p>❌ {error}</p>
              <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          )}
          
          {status === 'pack_selection' && packFiles && (
            <div className="pack-file-selector">
              <div className="pack-header">
                <h3>📦 Pack de películas detectado</h3>
                <p>Se encontraron {packFiles.length} películas. Selecciona la correcta:</p>
              </div>
              
              <div className="pack-search">
                <input
                  type="text"
                  placeholder="🔍 Buscar película..."
                  value={packSearchQuery}
                  onChange={(e) => setPackSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              
              <div className="pack-file-list">
                {getFilteredPackFiles().map((file, index) => (
                  <div 
                    key={file.id}
                    className={`pack-file-item ${file.matchScore > 0 ? 'recommended' : ''}`}
                    onClick={() => handlePackFileSelect(file)}
                  >
                    <div className="pack-file-info">
                      {file.matchScore > 0 && <span className="recommended-badge">⭐ Recomendada</span>}
                      <span className="pack-file-name">{file.filename}</span>
                    </div>
                    <span className="pack-file-size">{file.size}</span>
                  </div>
                ))}
                {getFilteredPackFiles().length === 0 && (
                  <p className="no-results">No se encontraron películas con ese nombre</p>
                )}
              </div>
              
              <div className="pack-footer">
                <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              </div>
            </div>
          )}
          
          {status === 'ready' && streamUrl && (
            <>
              <video
                ref={videoRef}
                className="debrid-video"
                controls
                playsInline
                autoPlay
                preload="auto"
                src={streamUrl}
                onError={(e) => {
                  console.error('Video error:', e.target.error)
                  const filename = streamUrl.split('/').pop() || ''
                  const isIncompatibleCodec = /x265|hevc|mkv/i.test(filename)
                  
                  if (isIncompatibleCodec) {
                    setError('Este formato (x265/MKV) no es compatible con el navegador. Usa el botón "Copy VLC URL" para reproducir en VLC.')
                  } else {
                    setError('Error al reproducir el video. Prueba con otro stream o usa VLC.')
                  }
                  setStatus('error')
                }}
              >
                {selectedSubtitle && selectedSubtitle.blobUrl && (
                  <track
                    kind="subtitles"
                    src={selectedSubtitle.blobUrl}
                    srcLang={selectedSubtitle.lang}
                    label={selectedSubtitle.langName}
                    default
                  />
                )}
              </video>
              
              {/* Audio track selector (if multiple tracks available) */}
              {audioTracks.length > 1 && (
                <div className="audio-track-selector">
                  <span className="audio-icon">🔊</span>
                  <select 
                    value={selectedAudioTrack || 0}
                    onChange={(e) => handleAudioTrackChange(parseInt(e.target.value))}
                    title="Pista de audio"
                  >
                    {audioTracks.map(track => (
                      <option key={track.index} value={track.index}>
                        {track.label || track.language}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Subtitle button */}
              <button 
                className="subtitle-btn" 
                onClick={() => setShowSubtitleMenu(!showSubtitleMenu)}
                title="Subtítulos (S)"
              >
                CC
              </button>
              
              {/* Subtitle menu */}
              {showSubtitleMenu && (
                <div className="subtitle-menu">
                  <div className="subtitle-menu-header">
                    <span>Subtítulos</span>
                    <button onClick={() => setShowSubtitleMenu(false)}>✕</button>
                  </div>
                  
                  {/* Source toggle */}
                  <div className="subtitle-source-toggle">
                    <button 
                      className={subtitleSource === 'opensubtitles' ? 'active' : ''}
                      onClick={() => setSubtitleSource('opensubtitles')}
                    >
                      OpenSub
                    </button>
                    <button 
                      className={subtitleSource === 'subdl' ? 'active' : ''}
                      onClick={() => {
                        if (isSubdlAvailable()) {
                          setSubtitleSource('subdl')
                        } else {
                          setShowApiKeyInput(true)
                        }
                      }}
                    >
                      Subdl
                    </button>
                  </div>
                  
                  {/* API Key input for Subdl */}
                  {showApiKeyInput && (
                    <div className="api-key-input">
                      <input
                        type="text"
                        placeholder="Subdl API Key"
                        value={apiKeyInput}
                        onChange={(e) => setApiKeyInput(e.target.value)}
                      />
                      <button onClick={() => {
                        if (apiKeyInput) {
                          setSubdlApiKey(apiKeyInput)
                          setShowApiKeyInput(false)
                          setSubtitleSource('subdl')
                        }
                      }}>
                        Guardar
                      </button>
                      <a href="https://subdl.com/panel/api" target="_blank" rel="noopener noreferrer">
                        Obtener API Key
                      </a>
                    </div>
                  )}
                  
                  {/* Loading indicator */}
                  {loadingSubtitles && (
                    <div className="subtitle-loading">Cargando subtítulos...</div>
                  )}
                  
                  {/* Offset controls */}
                  {selectedSubtitle && (
                    <div className="subtitle-offset">
                      <span>Ajuste: {subtitleOffset > 0 ? '+' : ''}{subtitleOffset}s</span>
                      <div className="offset-buttons">
                        <button onClick={() => setSubtitleOffset(prev => prev - 0.5)}>-0.5s</button>
                        <button onClick={() => setSubtitleOffset(0)}>Reset</button>
                        <button onClick={() => setSubtitleOffset(prev => prev + 0.5)}>+0.5s</button>
                        <button className="btn-advanced" onClick={openAdvancedSync}>⚙️ Avanzado</button>
                      </div>
                    </div>
                  )}
                  
                  {/* Subtitle styling controls */}
                  <div className="subtitle-styling">
                    <div className="style-row">
                      <label>Tamaño:</label>
                      <select 
                        value={subtitleStyle.fontSize}
                        onChange={(e) => updateSubtitleStyle('fontSize', e.target.value)}
                      >
                        <option value="16">Pequeño</option>
                        <option value="20">Mediano</option>
                        <option value="24">Normal</option>
                        <option value="28">Grande</option>
                        <option value="32">Muy grande</option>
                      </select>
                    </div>
                    <div className="style-row">
                      <label>Color:</label>
                      <input 
                        type="color" 
                        value={subtitleStyle.color}
                        onChange={(e) => updateSubtitleStyle('color', e.target.value)}
                      />
                    </div>
                    <div className="style-row">
                      <label>Borde:</label>
                      <select 
                        value={subtitleStyle.outline || 'black'}
                        onChange={(e) => updateSubtitleStyle('outline', e.target.value)}
                      >
                        <option value="black">Negro</option>
                        <option value="none">Sin borde</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Language filter */}
                  <div className="subtitle-lang-filter">
                    <label>Idioma:</label>
                    <select 
                      value={subtitleLangFilter} 
                      onChange={(e) => setSubtitleLangFilter(e.target.value)}
                    >
                      <option value="all">Todos</option>
                      {[...new Set(subtitles.map(s => s.lang))].map(lang => (
                        <option key={lang} value={lang}>
                          {subtitles.find(s => s.lang === lang)?.langName || lang}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="subtitle-menu-list">
                    <button 
                      className={`subtitle-option ${!selectedSubtitle ? 'active' : ''}`}
                      onClick={() => handleSubtitleSelect(null)}
                    >
                      Desactivados
                    </button>
                    {subtitles
                      .filter(sub => subtitleLangFilter === 'all' || sub.lang === subtitleLangFilter)
                      .slice(0, 25)
                      .map((sub, index) => (
                      <div key={index} className="subtitle-option-row">
                        <button
                          className={`subtitle-option ${selectedSubtitle?.id === sub.id ? 'active' : ''}`}
                          onClick={() => handleSubtitleSelect(sub)}
                          title={sub.release}
                        >
                          <span className="sub-lang">{sub.langName}</span>
                          <span className="sub-release">{sub.release?.slice(0, 25)}{sub.release?.length > 25 ? '...' : ''}</span>
                        </button>
                        <button 
                          className="sub-download-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(sub.url, '_blank')
                          }}
                          title="Descargar subtítulo"
                        >
                          ⬇
                        </button>
                      </div>
                    ))}
                    {subtitles.length === 0 && (
                      <p className="no-subtitles">No hay subtítulos disponibles</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Copy URL button (for VLC/IINA) */}
              <button 
                className="vlc-btn" 
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(streamUrl)
                    // Visual feedback
                    const btn = document.querySelector('.vlc-btn')
                    const original = btn.textContent
                    btn.textContent = '✓'
                    setTimeout(() => btn.textContent = original, 1500)
                  } catch (err) {
                    alert(`URL copiada:\n${streamUrl}`)
                  }
                }}
                title="Copiar URL para VLC (Archivo > Abrir ubicación de red)"
              >
                Copy VLC URL
              </button>
              
              <button 
                className="fullscreen-btn" 
                onClick={toggleFullscreen}
                title="Pantalla completa (F)"
              >
                {isFullscreen ? '⊡' : '⛶'}
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Advanced Sync Modal */}
      {showAdvancedSync && (
        <div className="advanced-sync-overlay" onClick={() => setShowAdvancedSync(false)}>
          <div className="advanced-sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sync-header">
              <h3>🎯 Sincronizar Subtítulos</h3>
              <div className="sync-controls">
                <button 
                  className="sync-btn" 
                  onClick={togglePlayPause}
                  title={isVideoPaused ? 'Reanudar' : 'Pausar'}
                >
                  {isVideoPaused ? '▶️' : '⏸️'}
                </button>
                <button 
                  className="sync-btn" 
                  onClick={scrollToCurrentCue}
                  title="Ir al momento actual"
                >
                  📍
                </button>
                <span className="sync-time">⏱️ {formatTime(currentVideoTime)}</span>
              </div>
              <button className="sync-close" onClick={() => setShowAdvancedSync(false)}>✕</button>
            </div>
            
            <div className="sync-instructions">
              <p>Haz clic en la línea que <strong>escuchas ahora mismo</strong> en la película</p>
              <p className="sync-offset-preview">Offset actual: <strong>{subtitleOffset > 0 ? '+' : ''}{subtitleOffset}s</strong></p>
            </div>
            
            <div className="sync-cue-list" ref={syncListRef}>
              {parsedCues.map((cue, index) => {
                const isCurrent = getCurrentCueIndex() === index
                
                return (
                  <div 
                    key={index}
                    className={`sync-cue ${isCurrent ? 'current' : ''}`}
                    onClick={() => handleSyncCueClick(cue)}
                  >
                    <span className="cue-time">{cue.startFormatted.substring(0, 8)}</span>
                    <span className="cue-text">{cue.text}</span>
                  </div>
                )
              })}
            </div>
            
            <div className="sync-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdvancedSync(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DebridPlayer
